import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Save, Eraser, Info, CheckCircle, AlertCircle, RefreshCw, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { API_URL, jsonAuthHeaders, readJsonOrThrow } from '../../utils/api';
import {
  enumerateNights, nightCount, addDays, weekdayOf, formatNightLabel, parsePriceInput, round2, MAX_NIGHTS,
  CHILD_FREE_MAX_AGE, CHILD_PAID_MAX_AGE
} from '../../utils/pricing';

// Fiyat = oda tipi + gece + YETİŞKİN sayısı (13+). 7-12 yaş çocuk ücreti oda kartındadır (Oda Yönetimi).

// Gün düğmeleri Pazartesi'den başlar; value = JS getDay (0=Pazar)
const WEEKDAY_BUTTONS = [
  { value: 1, label: 'Pzt' }, { value: 2, label: 'Sal' }, { value: 3, label: 'Çar' },
  { value: 4, label: 'Per' }, { value: 5, label: 'Cum' }, { value: 6, label: 'Cmt' }, { value: 0, label: 'Paz' },
];
const WEEKDAY_SHORT = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const WEEKEND_NIGHTS = [5, 6]; // Otelcilikte hafta sonu geceleri: Cuma + Cumartesi
const LARGE_BULK_CONFIRM = 200;
const MAX_MATRIX_PERSONS = 12;

const pad = (n) => String(n).padStart(2, '0');
const monthStart = (y, m) => `${y}-${pad(m + 1)}-01`;
const monthEnd = (y, m) => addDays(m === 11 ? monthStart(y + 1, 0) : monthStart(y, m + 1), -1);
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const formatPrice = (n) => Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 2 });
const cellKey = (roomId, persons) => `${roomId}|${persons}`;

// Bir odanın fiyatlanan kişi kademeleri: kapasite ve takvimde görülen en yüksek kişi sayısının büyüğü
const personsForRoom = (room, roomRates) => {
  const seen = Object.values(roomRates || {}).reduce((m, byP) => Math.max(m, ...Object.keys(byP).map(Number), 0), 0);
  const max = Math.min(MAX_MATRIX_PERSONS, Math.max(Number(room.adultCapacity) || 1, seen, 1));
  return Array.from({ length: max }, (_, i) => i + 1);
};

const median = (values) => {
  if (values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
};

// Yazım hatası yakalama: odanın bilinen fiyatlarının medyanına göre %20'nin altı ya da 5 katının üstü şüpheli
// (ör. "29.500" yerine "295"). Referans yoksa (görüntülenen ayda fiyat yok) 1.000 TL altı şüpheli sayılır.
const isImplausible = (price, reference) => {
  if (!reference) return price < 1000;
  return price < reference * 0.2 || price > reference * 5;
};

export default function RateCalendar() {
  const { currentUser } = useAuth();
  const { data, loading, errors, fetchData } = useData();
  const canWrite = !!currentUser?.permissions?.write;

  const today = localToday();
  const [view, setView] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [roomFilter, setRoomFilter] = useState('all');

  const [rates, setRates] = useState({});          // { [roomTypeId]: { 'YYYY-MM-DD': { [persons]: price } } }
  const [loadingRates, setLoadingRates] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const reqIdRef = useRef(0);                      // yalnızca en son isteğin yanıtı işlenir

  // Toplu (dönem) fiyat girişi
  const [bulkFrom, setBulkFrom] = useState(() => monthStart(view.year, view.month));
  const [bulkTo, setBulkTo] = useState(() => monthEnd(view.year, view.month));
  const [bulkDays, setBulkDays] = useState(ALL_DAYS);
  const [matrix, setMatrix] = useState({});        // { 'roomId|persons': '29.500' }
  const [clearRooms, setClearRooms] = useState([]); // "Fiyatları sil" için seçilen odalar
  const [saving, setSaving] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [notice, setNotice] = useState(null);      // { type: 'success'|'error', text }

  // Tek hücre düzenleme. editingRef, blur ve Enter'ın aynı düzenlemeyi iki kez kaydetmesini önler.
  const [editing, setEditing] = useState(null);    // { roomId, date, persons }
  const editingRef = useRef(null);
  const [cellDraft, setCellDraft] = useState('');
  const [savingCells, setSavingCells] = useState({}); // { 'roomId|date|persons': true }

  const roomTypesRequested = useRef(false);
  const roomTypes = useMemo(
    () => (Array.isArray(data.roomTypes) ? [...data.roomTypes].sort((a, b) => a.id - b.id) : []),
    [data.roomTypes]
  );
  const visibleRooms = roomFilter === 'all' ? roomTypes : roomTypes.filter(r => String(r.id) === roomFilter);
  const matrixPersons = useMemo(() => {
    const max = roomTypes.reduce((m, r) => Math.max(m, Number(r.adultCapacity) || 1), 1);
    return Array.from({ length: Math.min(MAX_MATRIX_PERSONS, max) }, (_, i) => i + 1);
  }, [roomTypes]);

  const monthFrom = monthStart(view.year, view.month);
  const monthTo = monthEnd(view.year, view.month);
  const days = useMemo(() => enumerateNights(monthFrom, addDays(monthTo, 1)), [monthFrom, monthTo]);
  const monthLabel = new Date(Date.UTC(view.year, view.month, 1))
    .toLocaleDateString('tr-TR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  // Odanın görüntülenen aydaki fiyatlarının medyanı (makullük kontrolü için)
  const roomReference = useMemo(() => {
    const ref = {};
    Object.entries(rates).forEach(([roomId, byDate]) => {
      ref[roomId] = median(Object.values(byDate).flatMap(byP => Object.values(byP)));
    });
    return ref;
  }, [rates]);

  useEffect(() => {
    roomTypesRequested.current = true;
    fetchData('roomTypes');
  }, [fetchData]);

  // Görüntülenen ayın fiyatları. Ay hızlıca değiştirilirse eski ayın geç gelen yanıtı yok sayılır.
  useEffect(() => {
    if (!currentUser?.token) return;
    const reqId = ++reqIdRef.current;
    setRates({});
    setLoadingRates(true);
    setLoadError('');
    (async () => {
      try {
        const res = await fetch(`${API_URL}/roomRates?from=${monthFrom}&to=${monthTo}`, {
          headers: { 'Authorization': `Bearer ${currentUser.token}` }
        });
        const rows = await readJsonOrThrow(res, 'Fiyat takvimi yüklenemedi.');
        if (reqId !== reqIdRef.current) return;
        const map = {};
        (rows || []).forEach(r => {
          const byDate = (map[r.roomTypeId] = map[r.roomTypeId] || {});
          const byPersons = (byDate[r.date] = byDate[r.date] || {});
          byPersons[r.persons] = r.price;
        });
        setRates(map);
      } catch (e) {
        if (reqId !== reqIdRef.current) return;
        setRates({});
        setLoadError(e.message);
      } finally {
        if (reqId === reqIdRef.current) setLoadingRates(false);
      }
    })();
  }, [currentUser?.token, monthFrom, monthTo, reloadKey]);

  const reloadRates = () => setReloadKey(k => k + 1);

  const cancelEdit = () => {
    editingRef.current = null;
    setEditing(null);
    setCellDraft('');
  };

  const shiftMonth = (delta) => {
    cancelEdit();
    setView(v => {
      const m = v.month + delta;
      return { year: v.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
    });
  };

  const goToday = () => {
    const d = new Date();
    cancelEdit();
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };

  const setBulkToViewedMonth = () => {
    setBulkFrom(monthFrom);
    setBulkTo(monthTo);
  };

  // Seçilen aralıkta gün filtresine uyan gece sayısı (sunucudaki hesapla aynı mantık).
  // Önce gün farkı hesaplanır; aralık çok uzunsa gün gün sayılmaz (yıl yazılırken arayüz donmasın).
  const bulkStats = useMemo(() => {
    if (!bulkFrom || !bulkTo || bulkFrom > bulkTo) return { span: 0, nights: 0 };
    const span = nightCount(bulkFrom, addDays(bulkTo, 1));
    if (span === 0) return { span: 0, nights: 0 };
    if (span > MAX_NIGHTS) return { span, nights: 0, tooLong: true };
    const nights = enumerateNights(bulkFrom, addDays(bulkTo, 1)).filter(d => bulkDays.includes(weekdayOf(d))).length;
    return { span, nights };
  }, [bulkFrom, bulkTo, bulkDays]);

  // Matristeki dolu hücreler → { entries, invalid }
  const matrixEntries = useMemo(() => {
    const entries = [];
    const invalid = [];
    Object.entries(matrix).forEach(([key, raw]) => {
      if (!raw || !raw.trim()) return;
      const [roomTypeId, persons] = key.split('|').map(Number);
      const price = parsePriceInput(raw);
      if (!Number.isFinite(price) || price <= 0) invalid.push({ roomTypeId, persons, raw });
      else entries.push({ roomTypeId, persons, price: round2(price) });
    });
    return { entries, invalid };
  }, [matrix]);

  const toggleBulkDay = (value) => {
    setBulkDays(prev => (prev.includes(value) ? prev.filter(x => x !== value) : [...prev, value]));
  };

  const validateDates = () => {
    if (!bulkFrom || !bulkTo) return 'Başlangıç ve bitiş tarihini seçin.';
    if (bulkFrom > bulkTo) return 'Başlangıç tarihi bitiş tarihinden sonra olamaz.';
    if (bulkStats.tooLong) return `Tek seferde en fazla ${MAX_NIGHTS} gün seçilebilir.`;
    if (bulkStats.span === 0) return 'Geçerli bir tarih aralığı seçin.';
    if (bulkDays.length === 0) return 'En az bir gün seçin.';
    if (bulkStats.nights === 0) return 'Seçilen aralıkta, seçilen günlere denk gelen tarih yok.';
    return null;
  };

  const datePayload = () => ({ from: bulkFrom, to: bulkTo, weekdays: bulkDays.length === 7 ? [] : bulkDays });

  const roomName = (id) => roomTypes.find(r => r.id === Number(id))?.name || `Oda ${id}`;

  // Başlangıç gecesinin mevcut fiyatlarını matrise doldurur (düzenleyip yeniden uygulamak için)
  const handlePrefill = async () => {
    if (!bulkFrom) return setNotice({ type: 'error', text: 'Önce başlangıç tarihini seçin.' });
    const hasValues = Object.values(matrix).some(v => v && v.trim());
    if (hasValues && !window.confirm('Tablodaki girdiğiniz değerler, başlangıç gecesinin mevcut fiyatlarıyla değiştirilecek. Devam edilsin mi?')) return;
    setPrefilling(true);
    setNotice(null);
    try {
      const res = await fetch(`${API_URL}/roomRates?from=${bulkFrom}&to=${bulkFrom}`, {
        headers: { 'Authorization': `Bearer ${currentUser.token}` }
      });
      const rows = await readJsonOrThrow(res, 'Fiyatlar getirilemedi.');
      const next = {};
      (rows || []).forEach(r => {
        const room = roomTypes.find(t => t.id === r.roomTypeId);
        // Matriste yalnızca kapasiteye kadar hücre çizilir; görünmeyen hücre doldurulup sessizce gönderilmesin
        if (!room || r.persons > (Number(room.adultCapacity) || 1) || r.persons > matrixPersons.length) return;
        next[cellKey(r.roomTypeId, r.persons)] = formatPrice(r.price);
      });
      setMatrix(next);
      setNotice({ type: 'success', text: rows.length
        ? `${formatNightLabel(bulkFrom)} gecesinin ${rows.length} fiyatı tabloya getirildi.`
        : `${formatNightLabel(bulkFrom)} gecesi için takvimde fiyat yok; tablo boşaltıldı.` });
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setPrefilling(false);
    }
  };

  const handleBulkApply = async (e) => {
    e.preventDefault();
    if (!canWrite) return;
    const problem = validateDates();
    if (problem) return setNotice({ type: 'error', text: problem });
    const { entries, invalid } = matrixEntries;
    if (invalid.length > 0) {
      return setNotice({ type: 'error', text: `Geçersiz fiyat: ${invalid.map(i => `${roomName(i.roomTypeId)} ${i.persons} kişi "${i.raw}"`).join(', ')}` });
    }
    if (entries.length === 0) return setNotice({ type: 'error', text: 'Tabloya en az bir fiyat girin (boş hücreler değiştirilmez).' });

    const suspicious = entries.filter(en => isImplausible(en.price, roomReference[en.roomTypeId]));
    if (suspicious.length > 0 && !window.confirm(
      `Şu fiyatlar olağandışı görünüyor:\n${suspicious.map(s => `• ${roomName(s.roomTypeId)} ${s.persons} kişi: ${formatPrice(s.price)} TL`).join('\n')}\n\nFiyatlar doğru mu?`
    )) return;
    const total = entries.length * bulkStats.nights;
    const dayNote = bulkDays.length === 7 ? 'her gece' : `yalnızca ${WEEKDAY_BUTTONS.filter(d => bulkDays.includes(d.value)).map(d => d.label).join(', ')} geceleri`;
    if (!window.confirm(
      `${bulkFrom} → ${bulkTo} (${dayNote}) arasında ${bulkStats.nights} gece için ${entries.length} fiyat yazılacak (${total} kayıt).` +
      (total > LARGE_BULK_CONFIRM ? '\n\nBu büyük bir toplu değişiklik.' : '') + '\n\nOnaylıyor musunuz?'
    )) return;

    setSaving(true);
    setNotice(null);
    try {
      const res = await fetch(`${API_URL}/roomRates/bulk`, {
        method: 'POST',
        headers: jsonAuthHeaders(currentUser.token),
        body: JSON.stringify({ ...datePayload(), entries })
      });
      const body = await readJsonOrThrow(res, 'Fiyatlar kaydedilemedi.');
      setNotice({ type: 'success', text: `${bulkStats.nights} gece × ${entries.length} fiyat kaydedildi (${body.count} kayıt).` });
      reloadRates();
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleBulkClear = async () => {
    if (!canWrite) return;
    const problem = validateDates();
    if (problem) return setNotice({ type: 'error', text: problem });
    if (clearRooms.length === 0) return setNotice({ type: 'error', text: 'Fiyatlarını silmek istediğiniz odaları soldaki kutucuklardan seçin.' });
    if (!window.confirm(
      `${clearRooms.map(roomName).join(', ')} için ${bulkStats.nights} gecenin TÜM kişi fiyatları silinecek; bu geceler tekliflerde 0 TL gelir. Onaylıyor musunuz?`
    )) return;

    setSaving(true);
    setNotice(null);
    try {
      const res = await fetch(`${API_URL}/roomRates/bulk-delete`, {
        method: 'POST',
        headers: jsonAuthHeaders(currentUser.token),
        body: JSON.stringify({ ...datePayload(), roomTypeIds: clearRooms })
      });
      const body = await readJsonOrThrow(res, 'Fiyatlar silinemedi.');
      setNotice({ type: 'success', text: `${body.count} fiyat kaydı silindi.` });
      setClearRooms([]);
      reloadRates();
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (roomId, date, persons) => {
    // Yüklenirken ya da yükleme hatasında ızgara gerçek fiyatları göstermiyor olabilir → düzenlemeye izin verme
    if (!canWrite || loadingRates || loadError || savingCells[`${roomId}|${date}|${persons}`]) return;
    editingRef.current = { roomId, date, persons };
    setEditing({ roomId, date, persons });
    const current = rates[roomId]?.[date]?.[persons];
    setCellDraft(current !== undefined ? formatPrice(current) : '');
  };

  // price === null → o gecenin bu kişi fiyatını sil
  const saveCell = async (roomId, date, persons, price) => {
    const key = `${roomId}|${date}|${persons}`;
    setSavingCells(prev => ({ ...prev, [key]: true }));
    setNotice(null);
    try {
      const res = price === null
        ? await fetch(`${API_URL}/roomRates/bulk-delete`, { method: 'POST', headers: jsonAuthHeaders(currentUser.token),
            body: JSON.stringify({ roomTypeIds: [roomId], from: date, to: date, persons: [persons] }) })
        : await fetch(`${API_URL}/roomRates/bulk`, { method: 'POST', headers: jsonAuthHeaders(currentUser.token),
            body: JSON.stringify({ from: date, to: date, entries: [{ roomTypeId: roomId, persons, price }] }) });
      await readJsonOrThrow(res, 'Fiyat kaydedilemedi.');
      setRates(prev => {
        const byDate = { ...(prev[roomId] || {}) };
        const byPersons = { ...(byDate[date] || {}) };
        if (price === null) delete byPersons[persons];
        else byPersons[persons] = price;
        if (Object.keys(byPersons).length === 0) delete byDate[date];
        else byDate[date] = byPersons;
        return { ...prev, [roomId]: byDate };
      });
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setSavingCells(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // trigger: 'enter' (açık kaydet) | 'blur' (başka yere tıklandı)
  //  - Boş + Enter → bu kişi fiyatını sil. Boş + blur → vazgeç (yanlışlıkla silinmesin).
  //  - Geçersiz + Enter → düzenleyici açık kalır. Geçersiz + blur → vazgeç ve uyar.
  const commitEdit = (trigger) => {
    const active = editingRef.current;
    if (!active) return;
    const { roomId, date, persons } = active;
    const existing = rates[roomId]?.[date]?.[persons];
    const trimmed = cellDraft.trim();
    const room = roomTypes.find(r => r.id === roomId);

    if (trimmed === '') {
      if (trigger === 'blur' || existing === undefined) return cancelEdit();
      cancelEdit();
      return saveCell(roomId, date, persons, null);
    }

    const parsed = parsePriceInput(trimmed);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setNotice({ type: 'error', text: `"${trimmed}" geçerli bir fiyat değil. Ör. 29500 ya da 29.500 yazın; boş bırakıp Enter bu fiyatı siler.` });
      if (trigger === 'blur') cancelEdit();
      return;
    }
    const price = round2(parsed);
    if (existing !== undefined && Number(existing) === price) return cancelEdit();

    if (isImplausible(price, roomReference[roomId]) && !window.confirm(
      `${room?.name || 'Oda'} · ${persons} kişi · ${formatNightLabel(date)}: ${formatPrice(price)} TL girdiniz. Fiyat doğru mu?`
    )) {
      if (trigger === 'blur') cancelEdit();
      return;
    }

    cancelEdit();
    saveCell(roomId, date, persons, price);
  };

  // Oda tipleri: null = henüz okunmadı (yükleniyor). Okuma başarısız olursa DataContext önceki listeyi (yoksa [])
  // korur ve errors.roomTypes'ı doldurur → elde hiç oda yoksa "Henüz oda tipi yok" yerine hata ekranı.
  const roomTypesError = errors?.roomTypes || '';
  if (!Array.isArray(data.roomTypes) || (roomTypesError && roomTypes.length === 0)) {
    const failed = !!roomTypesError && roomTypesRequested.current && !loading.roomTypes;
    if (!failed) return <LoadingSpinner />;
    return (
      <div className="max-w-xl mx-auto mt-10 p-6 bg-white dark:bg-gray-800 rounded-lg shadow text-center">
        <AlertCircle className="mx-auto mb-3 text-red-500" size={32} />
        <p className="text-gray-800 dark:text-gray-100 font-medium">Oda tipleri yüklenemedi.</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 break-words">{roomTypesError}</p>
        <button onClick={() => fetchData('roomTypes')} className="mt-4 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-2">
          <RefreshCw size={16} /> Yeniden dene
        </button>
      </div>
    );
  }

  const inputCls = 'w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white';
  const labelCls = 'block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2';

  return (
    <div className="max-w-full">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Fiyat Takvimi</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Gecelik oda fiyatları yetişkin sayısına göre girilir. {CHILD_FREE_MAX_AGE + 1}-{CHILD_PAID_MAX_AGE} yaş çocuk ücreti oda kartındadır, 0-{CHILD_FREE_MAX_AGE} yaş ücretsizdir.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftMonth(-1)} className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300" aria-label="Önceki ay">
            <ChevronLeft size={20} />
          </button>
          <span className="min-w-[140px] text-center font-semibold text-gray-800 dark:text-white capitalize" aria-live="polite">{monthLabel}</span>
          <button onClick={() => shiftMonth(1)} className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300" aria-label="Sonraki ay">
            <ChevronRight size={20} />
          </button>
          <button onClick={goToday} className="px-3 py-2 rounded-lg text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
            Bugün
          </button>
        </div>
      </div>

      {notice && (
        <div role={notice.type === 'error' ? 'alert' : 'status'} className={`mb-4 p-3 rounded-lg flex items-start gap-2 text-sm ${
          notice.type === 'success'
            ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
        }`}>
          {notice.type === 'success' ? <CheckCircle size={18} className="shrink-0 mt-0.5" /> : <AlertCircle size={18} className="shrink-0 mt-0.5" />}
          <span className="flex-1 whitespace-pre-line">{notice.text}</span>
          <button onClick={() => setNotice(null)} className="opacity-60 hover:opacity-100" aria-label="Bildirimi kapat">×</button>
        </div>
      )}

      {canWrite ? (
        <form onSubmit={handleBulkApply} className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow border border-gray-100 dark:border-gray-700 mb-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
            <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Dönem Fiyatı Gir</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Boş bıraktığınız hücreler değişmez. Aynı fiyatlı kademelere (ör. villalarda 1-4 kişi) aynı fiyatı yazın.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label htmlFor="bulk-from" className={labelCls}>Başlangıç</label>
              <input id="bulk-from" type="date" min="2000-01-01" max="2100-12-31" value={bulkFrom} onChange={e => setBulkFrom(e.target.value)} required className={inputCls} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="bulk-to" className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Bitiş (dahil)</label>
                <button type="button" onClick={setBulkToViewedMonth} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">Görüntülenen ay</button>
              </div>
              <input id="bulk-to" type="date" min="2000-01-01" max="2100-12-31" value={bulkTo} onChange={e => setBulkTo(e.target.value)} required className={inputCls} />
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Geceler</span>
                <div className="flex gap-3 text-xs">
                  <button type="button" onClick={() => setBulkDays(ALL_DAYS)} className="text-blue-600 dark:text-blue-400 hover:underline">Her gün</button>
                  <button type="button" onClick={() => setBulkDays([0, 1, 2, 3, 4])} className="text-blue-600 dark:text-blue-400 hover:underline">Hafta içi (Paz–Per)</button>
                  <button type="button" onClick={() => setBulkDays(WEEKEND_NIGHTS)} className="text-blue-600 dark:text-blue-400 hover:underline">Hafta sonu (Cum–Cmt)</button>
                </div>
              </div>
              <div className="flex gap-1.5">
                {WEEKDAY_BUTTONS.map(d => {
                  const on = bulkDays.includes(d.value);
                  return (
                    <button type="button" key={d.value} onClick={() => toggleBulkDay(d.value)} aria-pressed={on}
                      className={`flex-1 py-2 rounded text-sm font-medium border transition-colors ${
                        on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                      }`}>
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700">
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[190px]">
                    Oda Tipi <span className="normal-case font-normal">(kutucuk: silme için seç)</span>
                  </th>
                  {matrixPersons.map(p => (
                    <th scope="col" key={p} className="px-1 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 min-w-[92px]">{p} kişi</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roomTypes.map(room => {
                  const capacity = Number(room.adultCapacity) || 1;
                  const checked = clearRooms.includes(room.id);
                  return (
                    <tr key={room.id} className="border-t border-gray-100 dark:border-gray-700">
                      <th scope="row" className="px-3 py-1.5 text-left font-normal align-middle">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={checked}
                            onChange={() => setClearRooms(prev => (prev.includes(room.id) ? prev.filter(x => x !== room.id) : [...prev, room.id]))}
                            className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500" aria-label={`${room.name} fiyatlarını silmek için seç`} />
                          <span>
                            <span className="block font-medium text-gray-800 dark:text-gray-100 leading-tight">{room.name}</span>
                            <span className="block text-[11px] text-gray-400 dark:text-gray-500">
                              en fazla {capacity} yetişkin · çocuk 7-12: {room.childFee != null ? `${formatPrice(room.childFee)} TL` : 'tanımsız'}
                            </span>
                          </span>
                        </label>
                      </th>
                      {matrixPersons.map(p => {
                        const key = cellKey(room.id, p);
                        const raw = matrix[key] || '';
                        const bad = raw.trim() !== '' && !(parsePriceInput(raw) > 0);
                        return (
                          <td key={p} className="px-1 py-1.5 text-center">
                            {p <= capacity ? (
                              <input type="text" inputMode="decimal" autoComplete="off" value={raw}
                                onChange={e => setMatrix(prev => ({ ...prev, [key]: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                                aria-label={`${room.name} ${p} kişi gecelik fiyat (TL)`}
                                className={`w-full px-2 py-1.5 text-right border rounded dark:bg-gray-700 dark:text-white ${bad ? 'border-red-500 bg-red-50' : 'border-gray-300 dark:border-gray-600'}`} />
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600" title="Oda kapasitesinin üzerinde">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <p className="flex-1 text-sm text-gray-600 dark:text-gray-400">
              {bulkStats.tooLong
                ? <span className="text-red-600 dark:text-red-400">Tek seferde en fazla {MAX_NIGHTS} gün seçilebilir.</span>
                : <><strong className="text-gray-800 dark:text-gray-200">{bulkStats.nights}</strong> gece · <strong className="text-gray-800 dark:text-gray-200">{matrixEntries.entries.length}</strong> fiyat girildi
                    {matrixEntries.invalid.length > 0 && <span className="text-red-600 dark:text-red-400"> · {matrixEntries.invalid.length} geçersiz</span>}</>}
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={handlePrefill} disabled={saving || prefilling}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                title="Başlangıç gecesinin takvimdeki fiyatlarını tabloya getirir">
                <Download size={16} /> {prefilling ? 'Getiriliyor...' : 'Başlangıç gecesini getir'}
              </button>
              <button type="button" onClick={() => setMatrix({})} disabled={saving}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                Tabloyu temizle
              </button>
              <button type="button" onClick={handleBulkClear} disabled={saving}
                className="px-4 py-2 rounded-lg border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 disabled:opacity-50"
                title="Seçili odaların bu dönemdeki tüm kişi fiyatlarını siler">
                <Eraser size={16} /> Seçili odaların fiyatlarını sil
              </button>
              <button type="submit" disabled={saving}
                className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50">
                <Save size={16} /> {saving ? 'Kaydediliyor...' : 'Fiyatları Uygula'}
              </button>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-6 p-3 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
          <Info size={16} /> Görüntüleme modundasınız. Fiyat girmek için yazma yetkisi gerekir.
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-100 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
          <label className="flex items-center gap-2">
            <span className="font-medium text-gray-600 dark:text-gray-300">Oda:</span>
            <select value={roomFilter} onChange={e => { cancelEdit(); setRoomFilter(e.target.value); }}
              className="px-2 py-1 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white">
              <option value="all">Tüm odalar</option>
              {roomTypes.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
            </select>
          </label>
          <span className="flex items-center gap-1.5"><span className="font-semibold text-gray-800 dark:text-gray-100">29.500</span> takvim fiyatı</span>
          <span className="flex items-center gap-1.5"><span className="text-gray-400 dark:text-gray-500">—</span> fiyat tanımlı değil → teklifte 0</span>
          <span className="flex items-center gap-1.5"><span className="text-red-500 font-semibold">yok</span> o gece bu kişi sayısına fiyat yok</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800" /> Cuma/Cumartesi gecesi</span>
          {loadingRates && <span className="text-blue-600 dark:text-blue-400">Yükleniyor...</span>}
        </div>
        {canWrite && (
          <p className="px-4 pt-2 text-[11px] text-gray-400 dark:text-gray-500">
            Hücreye tıklayıp fiyat yazın · Enter kaydeder · boş bırakıp Enter o kişi fiyatını siler · Esc vazgeçer
          </p>
        )}

        {loadError && (
          <div className="px-4 py-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-900/40 flex items-center justify-between gap-3">
            <span>{loadError}</span>
            <button onClick={reloadRates} className="inline-flex items-center gap-1 text-red-700 dark:text-red-300 hover:underline shrink-0">
              <RefreshCw size={14} /> Yeniden dene
            </button>
          </div>
        )}

        {roomTypes.length === 0 ? (
          <p className="p-6 text-center text-gray-500 dark:text-gray-400">Henüz oda tipi yok. Önce Oda Yönetimi'nden oda ekleyin.</p>
        ) : (
          <div className={`overflow-x-auto transition-opacity ${loadingRates ? 'opacity-50' : ''}`}>
            <table className="border-collapse text-sm">
              <thead>
                <tr>
                  <th scope="col" className="sticky left-0 z-20 bg-gray-50 dark:bg-gray-700 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[170px] border-b border-r border-gray-200 dark:border-gray-600">
                    Oda / Kişi
                  </th>
                  {days.map(date => {
                    const wd = weekdayOf(date);
                    const weekend = WEEKEND_NIGHTS.includes(wd);
                    const isToday = date === today;
                    return (
                      <th scope="col" key={date} className={`px-1 py-2 text-center font-medium min-w-[64px] border-b border-gray-200 dark:border-gray-600 ${
                        isToday ? 'bg-blue-600 text-white' : weekend ? 'bg-amber-50 dark:bg-amber-900/20 text-gray-700 dark:text-gray-200' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}>
                        <div className="text-sm leading-tight">{Number(date.slice(8, 10))}</div>
                        <div className={`text-[10px] leading-tight ${isToday ? 'text-blue-100' : 'text-gray-400 dark:text-gray-400'}`}>{WEEKDAY_SHORT[wd]}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              {visibleRooms.map(room => {
                const roomRates = rates[room.id] || {};
                const personsList = personsForRoom(room, roomRates);
                return (
                  <tbody key={room.id}>
                    <tr>
                      <th scope="rowgroup" colSpan={days.length + 1} className="text-left bg-gray-100 dark:bg-gray-900 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700">
                        {/* Tam genişlikteki th sticky olamaz; içerik sticky → yatay kaydırmada oda adı solda kalır */}
                        <span className="sticky left-3 inline-block">
                        <span className="font-semibold text-gray-800 dark:text-gray-100">{room.name}</span>
                        <span className="ml-3 text-[11px] font-normal text-gray-500 dark:text-gray-400">
                          en fazla {Number(room.adultCapacity) || 1} yetişkin · çocuk 7-12: {room.childFee != null ? `${formatPrice(room.childFee)} TL` : 'tanımsız'}
                        </span>
                        </span>
                      </th>
                    </tr>
                    {personsList.map(persons => (
                      <tr key={persons} className="group">
                        <th scope="row" className="sticky left-0 z-10 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700 px-3 py-1 text-left font-normal text-gray-600 dark:text-gray-300 border-b border-r border-gray-100 dark:border-gray-700 whitespace-nowrap">
                          {persons} kişi{persons > (Number(room.adultCapacity) || 1) && <span className="ml-1 text-[10px] text-red-500">kapasite üstü</span>}
                        </th>
                        {days.map(date => {
                          const byPersons = roomRates[date];
                          const price = byPersons?.[persons];
                          const nightHasRates = byPersons && Object.keys(byPersons).length > 0;
                          const weekend = WEEKEND_NIGHTS.includes(weekdayOf(date));
                          const isEditing = editing?.roomId === room.id && editing?.date === date && editing?.persons === persons;
                          const isSaving = !!savingCells[`${room.id}|${date}|${persons}`];
                          const label = `${room.name}, ${persons} kişi, ${formatNightLabel(date)}`;
                          const display = price !== undefined
                            ? { node: <span className="font-semibold text-gray-800 dark:text-gray-100">{formatPrice(price)}</span>, aria: `${formatPrice(price)} TL` }
                            : nightHasRates
                              ? { node: <span className="text-red-500 font-semibold">yok</span>, aria: 'bu kişi sayısı için fiyat yok' }
                              : { node: <span className="text-gray-300 dark:text-gray-600">—</span>, aria: 'fiyat tanımlı değil, teklifte 0' };
                          return (
                            <td key={date} className={`p-0.5 text-center border-b border-gray-100 dark:border-gray-700 ${weekend ? 'bg-amber-50/60 dark:bg-amber-900/10' : ''} ${date === today ? 'ring-1 ring-inset ring-blue-400' : ''}`}>
                              {isEditing ? (
                                <input
                                  autoFocus
                                  type="text"
                                  inputMode="decimal"
                                  autoComplete="off"
                                  aria-label={`${label} gecelik fiyat (TL)`}
                                  value={cellDraft}
                                  onChange={e => setCellDraft(e.target.value)}
                                  onBlur={() => {
                                    // Pencere/sekme değişiminde kaydetme; düzenleyici açık kalsın
                                    if (!document.hasFocus()) return;
                                    commitEdit('blur');
                                  }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') { e.preventDefault(); commitEdit('enter'); }
                                    if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                                  }}
                                  className="w-full min-w-[56px] px-1 py-1 text-center text-sm border border-blue-500 rounded outline-none dark:bg-gray-700 dark:text-white"
                                />
                              ) : isSaving ? (
                                <span className="block py-1 text-xs text-blue-600 dark:text-blue-400" aria-label={`${label} kaydediliyor`}>...</span>
                              ) : canWrite ? (
                                <button type="button" onClick={() => startEdit(room.id, date, persons)} aria-label={`${label}: ${display.aria}. Düzenle`}
                                  title={label} disabled={loadingRates || !!loadError}
                                  className="w-full px-1 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                  {display.node}
                                </button>
                              ) : (
                                <span className="block px-1 py-1">{display.node}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                );
              })}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
